import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Plus, Copy, Check, Calendar, MapPin, Users, Loader2, Trash2, Edit2, ShieldOff, ShieldCheck, RotateCw, FileSpreadsheet, Ticket, Receipt, TrendingUp } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import EventFormDialog from '../components/admin/EventFormDialog';
import { InternalListPanel } from '../components/organizer/InternalListPanel';
import TicketSalesDialog from '../components/organizer/TicketSalesDialog';
import OfflineTicketDialog from '../components/organizer/OfflineTicketDialog';
import PromoterAnalyticsPanel from '../components/common/PromoterAnalyticsPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL_FORM = {
  title: '',
  date: '',
  time: '',
  venue: '',
  address: '',
  genre: '',
  description: '',
  image_url: '',
  lineup: '',
  is_featured: false,
  waitlist_only: false,
  min_age: 21,
  terms_conditions: '',
  event_type: 'ticketed',
  waitlist_categories: ['stag_male'],
  ticket_tiers: [{ name: '', price: '', quantity_available: '', description: '' }],
};

/**
 * Event organizer dashboard: create events, share event codes with promoters,
 * and see every guest list that has spawned under each event.
 */
const OrganizerDashboard = () => {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create / edit dialog
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);

  // Guest lists drilldown — backed by the organizer-scoped endpoint which
  // returns every list under the event + every guest row + a per-promoter
  // breakdown. Read-only — organizers can't edit a promoter's list.
  const [drillEventId, setDrillEventId] = useState(null);
  const [drillData, setDrillData] = useState(null);  // { event, totals, lists: [{...gl, promoter, guests}] }
  const [expandedListIds, setExpandedListIds] = useState(new Set());

  const [copiedCode, setCopiedCode] = useState(null);
  // Ticket Sales dialog — organizer-safe sales + buyer-contacts view.
  const [salesEvent, setSalesEvent] = useState(null);
  // Offline ticketing dialog — organizer can issue door tickets per event (Cash/UPI/Card).
  const [offlineEvent, setOfflineEvent] = useState(null);
  // Top-level tab (events management vs promoter analytics)
  const [activeTab, setActiveTab] = useState('events');

  // Auth guard
  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else if (!user.is_organizer && !user.is_admin) {
        toast.error('Event organizer access required');
        navigate('/');
      }
    }
  }, [user, authLoading, navigate]);

  const fetchEvents = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/organizer/events`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEvents(res.data);
    } catch {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  // Silent refetch — used by sub-dialogs so a background refresh does not
  // toggle the global `loading` spinner (which unmounts the dialog and wipes
  // its success state).
  const refreshEventsSilently = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/organizer/events`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEvents(res.data);
      // Keep the open OfflineTicketDialog's `event` prop in sync so the
      // inventory counts refresh after a successful issue.
      setOfflineEvent((cur) => cur ? (res.data.find((e) => e.id === cur.id) || cur) : cur);
    } catch {
      /* non-fatal: the next full load will reconcile */
    }
  };

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${API}/organizer/events`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (active) setEvents(res.data);
      } catch {
        if (active) toast.error('Failed to load events');
      } finally {
        if (active) setLoading(false);
      }
    };
    if (token && (user?.is_organizer || user?.is_admin)) load();
    return () => { active = false; };
  }, [token, user?.is_organizer, user?.is_admin]);

  const handleOpenCreate = () => {
    setEditingEvent(null);
    setFormData(INITIAL_FORM);
    setShowEventModal(true);
  };

  const handleOpenEdit = (ev) => {
    setEditingEvent(ev);
    setFormData({
      title: ev.title,
      date: ev.date,
      time: ev.time,
      venue: ev.venue,
      address: ev.address,
      genre: ev.genre,
      description: ev.description,
      image_url: ev.image_url,
      lineup: (ev.lineup || []).join(', '),
      is_featured: !!ev.is_featured,
      waitlist_only: !!ev.waitlist_only,
      min_age: ev.min_age ?? 21,
      terms_conditions: ev.terms_conditions || '',
      event_type: ev.event_type || 'ticketed',
      waitlist_categories: Array.isArray(ev.waitlist_categories) ? ev.waitlist_categories : ['stag_male'],
      ticket_tiers: (ev.ticket_tiers || []).map((t) => ({
        id: t.id,
        name: t.name,
        price: String(t.price ?? ''),
        quantity_available: String(t.quantity_available ?? ''),
        description: t.description || '',
      })),
    });
    setShowEventModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        min_age: parseInt(formData.min_age) || 21,
        terms_conditions: (formData.terms_conditions || '').trim() || null,
        event_type: formData.event_type || 'ticketed',
        lineup: formData.lineup.split(',').map((a) => a.trim()).filter(Boolean),
        ticket_tiers: (formData.event_type === 'guest_list' ? [] : formData.ticket_tiers
          .filter((t) => t.name)
          .map((t) => ({
            ...(t.id && { id: t.id }),
            name: t.name,
            price: parseFloat(t.price) || 0,
            quantity_available: parseInt(t.quantity_available) || 0,
            description: t.description || null,
          }))),
      };
      const url = editingEvent ? `${API}/events/${editingEvent.id}` : `${API}/events`;
      const method = editingEvent ? 'put' : 'post';
      await axios[method](url, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(editingEvent ? 'Event updated' : 'Event created');
      setShowEventModal(false);
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/events/${ev.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Event deleted');
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Event code copied');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const openDrill = async (ev) => {
    setDrillEventId(ev.id);
    setDrillData(null);
    setExpandedListIds(new Set());
    try {
      const res = await axios.get(`${API}/organizer/events/${ev.id}/guest-lists`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDrillData(res.data);
    } catch {
      toast.error('Failed to load guest lists');
    }
  };

  const toggleListExpansion = (listId) => {
    setExpandedListIds((prev) => {
      const next = new Set(prev);
      if (next.has(listId)) next.delete(listId);
      else next.add(listId);
      return next;
    });
  };

  const handleKillOrReactivate = async (ev) => {
    const isKilled = !!ev.qrs_killed_at;
    const action = isKilled ? 'reactivate-qrs' : 'kill-qrs';
    const verb = isKilled ? 'reactivate' : 'kill';
    if (!isKilled && !window.confirm(
      `Kill every QR for "${ev.title}"?\n\n` +
      `Every guest QR + ticket QR issued so far will be REJECTED at the door. ` +
      `Guests can re-fetch a fresh QR after you reactivate. Use this only if a ` +
      `code has leaked or you need to lock down the door.`,
    )) return;
    try {
      await axios.post(`${API}/events/${ev.id}/${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(isKilled ? 'All QRs reactivated' : 'All QRs revoked');
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${verb}`);
    }
  };

  const handleRotateCode = async (ev) => {
    if (!window.confirm(
      `Rotate the event code for "${ev.title}"?\n\n` +
      `The current code (${ev.event_code}) and every promoter's RSVP URL ` +
      `(${ev.event_code}-P001, -P002, ...) will become invalid. Promoters need to ` +
      `re-share the new URLs with their guests.`,
    )) return;
    try {
      await axios.post(`${API}/events/${ev.id}/rotate-code`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Event code rotated');
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to rotate');
    }
  };

  const handleDownloadExcel = async (ev) => {
    try {
      const res = await axios.get(`${API}/organizer/events/${ev.id}/guests.xlsx`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      // Trigger a download via a temporary <a> — cleaner than window.open which some browsers block
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const safe = (ev.title || 'guests').replace(/[^\w\- ]+/g, '').replace(/\s+/g, '_');
      a.download = `${safe}_guests.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-[#B5FF00]" />
      </div>
    );
  }

  const drillEvent = events.find((e) => e.id === drillEventId);

  return (
    <div className="min-h-screen bg-[#050505] pt-20 sm:pt-24 pb-16" data-testid="organizer-dashboard">
      <div className="container mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 sm:gap-6 mb-8 sm:mb-10">
          <div>
            <span className="font-mono text-xs tracking-widest text-[#B5FF00] mb-2 block">
              EVENT ORGANIZER
            </span>
            <h1 className="font-['Josefin_Sans'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white">
              YOUR EVENTS
            </h1>
            <p className="text-zinc-400 mt-2 max-w-xl text-sm sm:text-base">
              Create events, share the event code with your promoters — they enter it to spawn their own guest list.
            </p>
          </div>
          <button
            onClick={handleOpenCreate}
            className="btn-primary flex items-center justify-center gap-2 w-full md:w-auto"
            data-testid="organizer-create-event-btn"
          >
            <Plus size={18} /> NEW EVENT
          </button>
        </div>

        {/* Top-level tabs: Events vs Promoter Analytics */}
        <div className="flex gap-6 border-b border-zinc-800 mb-6 overflow-x-auto" data-testid="organizer-tabs">
          <button
            onClick={() => setActiveTab('events')}
            className={`pb-3 px-1 font-mono text-sm tracking-widest whitespace-nowrap transition-colors flex items-center gap-2 ${
              activeTab === 'events'
                ? 'text-[#B5FF00] border-b-2 border-[#B5FF00]'
                : 'text-zinc-500 hover:text-white'
            }`}
            data-testid="organizer-tab-events"
          >
            <Calendar size={16} /> EVENTS
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`pb-3 px-1 font-mono text-sm tracking-widest whitespace-nowrap transition-colors flex items-center gap-2 ${
              activeTab === 'analytics'
                ? 'text-[#B5FF00] border-b-2 border-[#B5FF00]'
                : 'text-zinc-500 hover:text-white'
            }`}
            data-testid="organizer-tab-analytics"
          >
            <TrendingUp size={16} /> PROMOTERS
          </button>
        </div>

        {activeTab === 'analytics' && (
          <PromoterAnalyticsPanel token={token} events={events} />
        )}

        {activeTab === 'events' && (events.length === 0 ? (
          <div className="border border-dashed border-zinc-800 p-12 text-center" data-testid="organizer-empty">
            <Calendar size={42} className="text-zinc-700 mx-auto mb-4" />
            <h3 className="font-['Josefin_Sans'] text-2xl text-white mb-2">No events yet</h3>
            <p className="text-zinc-500 mb-6">Spin up your first event — promoters will start joining within minutes.</p>
            <button onClick={handleOpenCreate} className="btn-primary inline-flex items-center gap-2">
              <Plus size={18} /> CREATE FIRST EVENT
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {events.map((ev) => (
              <div
                key={ev.id}
                className="border border-zinc-800 bg-[#0A0A0A] overflow-hidden hover:border-zinc-700 transition-colors"
                data-testid={`organizer-event-card-${ev.id}`}
              >
                {ev.image_url && (
                  <div className="h-32 overflow-hidden">
                    <img src={ev.image_url} alt="" className="w-full h-full object-cover img-grayscale" />
                  </div>
                )}
                <div className="p-5">
                  <h3 className="font-['Josefin_Sans'] text-2xl text-white mb-2">{ev.title}</h3>
                  <div className="text-zinc-400 text-sm font-mono mb-1 flex items-center gap-2">
                    <Calendar size={14} /> {ev.date} · {ev.time}
                  </div>
                  <div className="text-zinc-400 text-sm font-mono mb-4 flex items-center gap-2">
                    <MapPin size={14} /> {ev.venue}
                  </div>

                  {/* Event code share block */}
                  <div className="border border-[#B5FF00]/30 bg-[#B5FF00]/5 p-3 mb-4">
                    <div className="font-mono text-xs text-zinc-400 tracking-widest mb-1">
                      PROMOTER EVENT CODE
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <code
                        className="text-[#B5FF00] font-mono text-base font-bold break-all"
                        data-testid={`organizer-event-code-${ev.id}`}
                      >
                        {ev.event_code || '—'}
                      </code>
                      {ev.event_code && (
                        <button
                          onClick={() => handleCopy(ev.event_code)}
                          className="flex-shrink-0 text-zinc-400 hover:text-[#B5FF00] p-1"
                          title="Copy code"
                          data-testid={`organizer-event-code-copy-${ev.id}`}
                        >
                          {copiedCode === ev.event_code ? <Check size={16} /> : <Copy size={16} />}
                        </button>
                      )}
                    </div>
                    <p className="text-zinc-500 text-xs mt-2 font-mono">
                      Share with promoters · they enter this on their dashboard to join.
                    </p>
                  </div>

                  {/* Event type + kill-switch state badges */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className={`font-mono text-[10px] tracking-widest px-2 py-1 border ${
                      ev.event_type === 'guest_list'
                        ? 'border-[#B5FF00]/40 bg-[#B5FF00]/5 text-[#B5FF00]'
                        : 'border-zinc-800 bg-zinc-900 text-zinc-300'
                    }`}>
                      {ev.event_type === 'guest_list' ? 'GUEST LIST ONLY' : 'TICKETED'}
                    </span>
                    {ev.qrs_killed_at && (
                      <span className="font-mono text-[10px] tracking-widest px-2 py-1 border border-red-500/40 bg-red-500/5 text-red-400" data-testid={`organizer-killed-badge-${ev.id}`}>
                        QRs REVOKED
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 mb-2">
                    <button
                      onClick={() => openDrill(ev)}
                      className="flex-1 border border-zinc-800 text-white text-sm font-mono tracking-widest py-2 hover:bg-[#B5FF00]/10 hover:border-[#B5FF00]/40 transition-colors flex items-center justify-center gap-2"
                      data-testid={`organizer-view-lists-${ev.id}`}
                    >
                      <Users size={14} /> GUEST LISTS
                    </button>
                    {ev.event_type !== 'guest_list' && (
                      <button
                        onClick={() => setSalesEvent(ev)}
                        className="flex-1 border border-zinc-800 text-white text-sm font-mono tracking-widest py-2 hover:bg-[#B5FF00]/10 hover:border-[#B5FF00]/40 transition-colors flex items-center justify-center gap-2"
                        data-testid={`organizer-view-sales-${ev.id}`}
                      >
                        <Ticket size={14} /> TICKET SALES
                      </button>
                    )}
                    {ev.event_type !== 'guest_list' && (
                      <button
                        onClick={() => setOfflineEvent(ev)}
                        className="border border-zinc-800 text-white hover:bg-[#B5FF00]/10 hover:border-[#B5FF00]/40 p-2 transition-colors"
                        data-testid={`organizer-offline-${ev.id}`}
                        title="Issue offline ticket (Cash/UPI/Card)"
                      >
                        <Receipt size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => handleDownloadExcel(ev)}
                      className="border border-zinc-800 text-white hover:bg-[#B5FF00]/10 hover:border-[#B5FF00]/40 p-2 transition-colors"
                      data-testid={`organizer-download-excel-${ev.id}`}
                      title="Download all guests as Excel"
                    >
                      <FileSpreadsheet size={14} />
                    </button>
                    <button
                      onClick={() => handleOpenEdit(ev)}
                      className="border border-zinc-800 text-zinc-400 hover:text-white p-2"
                      data-testid={`organizer-edit-${ev.id}`}
                      title="Edit event"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(ev)}
                      className="border border-zinc-800 text-zinc-400 hover:text-red-400 p-2"
                      data-testid={`organizer-delete-${ev.id}`}
                      title="Delete event"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>

                  {/* Security row: kill switch + code rotation */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => handleKillOrReactivate(ev)}
                      className={`text-xs font-mono tracking-widest py-2 border transition-colors flex items-center justify-center gap-2 ${
                        ev.qrs_killed_at
                          ? 'border-[#B5FF00]/40 bg-[#B5FF00]/5 text-[#B5FF00] hover:bg-[#B5FF00]/10'
                          : 'border-red-500/30 text-red-400 hover:bg-red-500/5'
                      }`}
                      data-testid={`organizer-kill-toggle-${ev.id}`}
                    >
                      {ev.qrs_killed_at ? (<><ShieldCheck size={14} /> REACTIVATE QRs</>) : (<><ShieldOff size={14} /> KILL ALL QRs</>)}
                    </button>
                    <button
                      onClick={() => handleRotateCode(ev)}
                      className="text-xs font-mono tracking-widest py-2 border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600 transition-colors flex items-center justify-center gap-2"
                      data-testid={`organizer-rotate-code-${ev.id}`}
                    >
                      <RotateCw size={14} /> ROTATE CODE
                    </button>
                  </div>

                  {/* Internal guest list — admin/internal-only. Standard promoters see nothing here. */}
                  <InternalListPanel
                    eventId={ev.id}
                    eventTitle={ev.title}
                    token={token}
                    user={user}
                  />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Event create/edit dialog */}
      <EventFormDialog
        open={showEventModal}
        onOpenChange={setShowEventModal}
        editingEvent={editingEvent}
        formData={formData}
        setFormData={setFormData}
        saving={false}
        onSubmit={handleSubmit}
        onCancel={() => setShowEventModal(false)}
        onAddTier={() => setFormData((f) => ({
          ...f,
          ticket_tiers: [...f.ticket_tiers, { name: '', price: '', quantity_available: '', description: '' }],
        }))}
        onRemoveTier={(idx) => setFormData((f) => ({
          ...f,
          ticket_tiers: f.ticket_tiers.filter((_, i) => i !== idx),
        }))}
        onTierChange={(idx, field, value) => setFormData((f) => ({
          ...f,
          ticket_tiers: f.ticket_tiers.map((t, i) => (i === idx ? { ...t, [field]: value } : t)),
        }))}
      />

      {/* Guest lists drilldown — full visibility for the organizer */}
      <Dialog open={!!drillEventId} onOpenChange={(o) => { if (!o) { setDrillEventId(null); setDrillData(null); } }}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 w-[calc(100vw-1.5rem)] sm:max-w-4xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="font-['Josefin_Sans'] text-xl sm:text-2xl text-white">
              GUEST LISTS — {drillEvent?.title?.toUpperCase()}
            </DialogTitle>
          </DialogHeader>

          {!drillData ? (
            <div className="py-8 flex justify-center">
              <Loader2 size={24} className="animate-spin text-[#B5FF00]" />
            </div>
          ) : drillData.lists.length === 0 ? (
            <p className="text-zinc-500 font-mono text-sm py-6 text-center">
              No promoter has joined this event yet. Share the event code to get started.
            </p>
          ) : (
            <>
              {/* Event-wide totals */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3 mt-2 mb-5" data-testid="organizer-drill-totals">
                {[
                  { label: 'LISTS', val: drillData.totals.lists, color: 'text-white' },
                  { label: 'PROMOTERS', val: drillData.totals.promoters, color: 'text-white' },
                  { label: 'TOTAL GUESTS', val: drillData.totals.guests, color: 'text-white' },
                  { label: 'CHECKED IN', val: drillData.totals.checked_in, color: 'text-[#B5FF00]' },
                  { label: 'WAITLIST', val: drillData.totals.waitlisted, color: 'text-amber-400' },
                ].map((s) => (
                  <div key={s.label} className="border border-zinc-800 p-3 text-center">
                    <div className="font-mono text-[10px] text-zinc-500 tracking-widest">{s.label}</div>
                    <div className={`text-xl sm:text-2xl font-bold ${s.color}`}>{s.val}</div>
                  </div>
                ))}
              </div>

              {/* Per-list cards with expandable guest rows */}
              <div className="space-y-3">
                {drillData.lists.map((gl) => {
                  const expanded = expandedListIds.has(gl.id);
                  return (
                    <div
                      key={gl.id}
                      className="border border-zinc-800"
                      data-testid={`organizer-drill-list-${gl.id}`}
                    >
                      <button
                        type="button"
                        onClick={() => toggleListExpansion(gl.id)}
                        className="w-full p-3 sm:p-4 text-left hover:bg-[#B5FF00]/5 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <h4 className="font-['Josefin_Sans'] text-lg text-white truncate">{gl.name}</h4>
                            <code className="text-[#B5FF00] font-mono text-xs break-all">{gl.code}</code>
                            <p className="text-zinc-500 font-mono text-xs mt-1 truncate">
                              by {gl.promoter?.name || 'Unknown'} {gl.promoter?.email ? `· ${gl.promoter.email}` : ''}
                            </p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="font-mono text-xs text-zinc-500 tracking-widest">GUESTS</div>
                            <div className="text-2xl font-bold text-white">{gl.guest_count}</div>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 mt-3 text-xs font-mono">
                          <div className="border border-zinc-800 p-2 text-center">
                            <div className="text-zinc-500">CONFIRMED</div>
                            <div className="text-white">{gl.confirmed_count}</div>
                          </div>
                          <div className="border border-zinc-800 p-2 text-center">
                            <div className="text-zinc-500">CHECKED IN</div>
                            <div className="text-[#B5FF00]">{gl.checked_in_count}</div>
                          </div>
                          <div className="border border-zinc-800 p-2 text-center">
                            <div className="text-zinc-500">WAITLIST</div>
                            <div className="text-amber-400">{gl.waitlist_count}</div>
                          </div>
                        </div>
                        <p className="text-zinc-500 font-mono text-[10px] tracking-widest mt-2 text-center">
                          {expanded ? '▲ HIDE GUESTS' : '▼ SHOW GUEST NAMES'}
                        </p>
                      </button>

                      {expanded && (
                        <div
                          className="border-t border-zinc-800 bg-[#050505]"
                          data-testid={`organizer-drill-list-guests-${gl.id}`}
                        >
                          {gl.guests.length === 0 ? (
                            <p className="text-zinc-500 font-mono text-xs p-4 text-center">No guests on this list yet.</p>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs sm:text-sm">
                                <thead>
                                  <tr className="text-left text-zinc-500 font-mono tracking-widest border-b border-zinc-800">
                                    <th className="p-2 sm:p-3 w-8">#</th>
                                    <th className="p-2 sm:p-3">NAME</th>
                                    <th className="p-2 sm:p-3 hidden sm:table-cell">CONTACT</th>
                                    <th className="p-2 sm:p-3">STATUS</th>
                                    <th className="p-2 sm:p-3 hidden md:table-cell">ADDED</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {gl.guests.map((g) => (
                                    <tr key={g.id} className="border-b border-zinc-900 hover:bg-zinc-900/30">
                                      <td className="p-2 sm:p-3 text-zinc-500 font-mono">{g.slot_number || '—'}</td>
                                      <td className="p-2 sm:p-3 text-white">
                                        <div className="truncate max-w-[160px]">{g.name}</div>
                                        <div className="sm:hidden text-zinc-500 text-xs truncate max-w-[160px]">
                                          {g.phone || g.email || ''}
                                        </div>
                                      </td>
                                      <td className="p-2 sm:p-3 text-zinc-300 hidden sm:table-cell">
                                        <div className="truncate max-w-[180px]">{g.phone || '—'}</div>
                                        <div className="text-zinc-500 truncate max-w-[180px]">{g.email || ''}</div>
                                      </td>
                                      <td className="p-2 sm:p-3">
                                        {g.checked_in ? (
                                          <span className="font-mono text-[10px] tracking-widest text-[#B5FF00]">CHECKED IN</span>
                                        ) : g.status === 'waitlisted' ? (
                                          <span className="font-mono text-[10px] tracking-widest text-amber-400">WAITLIST</span>
                                        ) : (
                                          <span className="font-mono text-[10px] tracking-widest text-white">CONFIRMED</span>
                                        )}
                                      </td>
                                      <td className="p-2 sm:p-3 text-zinc-500 font-mono text-xs hidden md:table-cell">
                                        {g.added_at ? new Date(g.added_at).toLocaleDateString() : '—'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Organizer-safe ticket sales dialog (per-event tier + buyer rows) */}
      <TicketSalesDialog
        open={!!salesEvent}
        onOpenChange={(o) => !o && setSalesEvent(null)}
        event={salesEvent}
        token={token}
      />

      {/* Offline ticketing dialog — issue tickets at the venue (Cash/UPI/Card) */}
      <OfflineTicketDialog
        open={!!offlineEvent}
        onOpenChange={(o) => !o && setOfflineEvent(null)}
        event={offlineEvent}
        token={token}
        onIssued={() => refreshEventsSilently()}
      />
    </div>
  );
};

export default OrganizerDashboard;
